import NumberInput from '@wandb/weave/common/components/elements/NumberInput';
import React, {useCallback} from 'react';

import {Analytics} from '../../../../services/analytics';
import {SettingLabel} from '../components/SettingLabel';
import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../types';
import {useSemanticLegendSettingsContext} from './SemanticLegendSettingsWithCategoricalConfigContextProvider';
import {
  MAX_NUMBER_OF_BUCKETS,
  MIN_NUMBER_OF_BUCKETS,
  MIN_NUMBER_OF_CATEGORIES,
} from './utils/recalculateSemanticLegendBuckets';

export type SemanticLegendNumBucketsProps = {
  numberOfBuckets: number;
  updateSetting: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
};

export const SemanticLegendNumBucketsWithCategoricalConfig = ({
  updateSetting,
  numberOfBuckets,
}: SemanticLegendNumBucketsProps) => {
  const {semanticLegendSettings} = useSemanticLegendSettingsContext();
  const updateSettingValue = useCallback(
    (newVal?: number) => {
      if (newVal == null) {
        newVal = 4;
      }
      Analytics.track('Semantic legend number of buckets changed', {
        newValue: newVal,
      });
      updateSetting({
        numberOfBuckets: newVal,
        shouldRecalculateBuckets: true, // we want to recalculate the buckets bounds when the number of buckets changes
      });
    },
    [updateSetting]
  );
  const maxNumberOfBuckets =
    semanticLegendSettings.yAxisValue === YAxisValues.Config_Categories
      ? semanticLegendSettings.allCategories?.length
      : MAX_NUMBER_OF_BUCKETS;
  const minNumberOfBuckets =
    semanticLegendSettings.yAxisValue === YAxisValues.Config_Categories
      ? MIN_NUMBER_OF_CATEGORIES
      : MIN_NUMBER_OF_BUCKETS;
  const title =
    semanticLegendSettings.yAxisValue === YAxisValues.Config_Categories
      ? 'Number of categories'
      : 'Number of buckets';
  return (
    <div
      className="flex w-full items-center gap-20 py-16 justify-between"
      data-test="num-buckets-setting">
      <SettingLabel text={title} className="min-w-fit" />
      <NumberInput
        placeholder={title}
        min={minNumberOfBuckets}
        max={maxNumberOfBuckets}
        stepper={true}
        value={numberOfBuckets}
        onChange={updateSettingValue}
        useStepperPlusMinus
      />
    </div>
  );
};
